import { boundOnce } from "./bound-once.js";
import { lijuItemsPath } from "../user/save-liju-routes.js";
import { drawSavedLiju } from "./save-liju-draw.js";
import { hasSession, listenForSavedLiju } from "./save-liju-press.js";
import type { StandingPanel } from "./study-standing.js";
import { openWhereRoom } from "./study-standing-badge.js";

const controlSelector = ".cb-save-liju";

/** The controls this bundle has not already bound. See `boundOnce`. */
const notYetListening = boundOnce();

async function held(
  fetched: typeof fetch,
  uuids: readonly string[],
): Promise<readonly string[]> {
  const query = new URLSearchParams({ ids: uuids.join(",") });
  const response = await fetched(`${lijuItemsPath}?${query.toString()}`);
  const body = (await response.json()) as { saved?: readonly string[] | null };

  return body.saved ?? [];
}

/**
 * Takes the presses on every saved-sentence control on the page.
 *
 * Safe to call again over a region htmx has replaced, for the reason
 * `startSaveWords` gives at greater length. `within` is that region.
 * @param document - The page the bundle is running on.
 * @param fetched - What asks the route, which a test hands its own.
 * @param within - Where to look, where that is not the whole page.
 */
export function startSaveLiju(
  document: Document,
  fetched: typeof fetch = globalThis.fetch,
  within: ParentNode = document,
): void {
  const controls = notYetListening(within, controlSelector);
  const buttons = controls.flatMap((control) => {
    const button = control.querySelector<HTMLButtonElement>(
      ".cb-save-liju-action",
    );
    return button === null ? [] : [button];
  });

  const panels = new Map<HTMLButtonElement, StandingPanel | undefined>();

  for (const control of controls) {
    const form = control.querySelector<HTMLFormElement>(".cb-save-liju-form");
    const button = control.querySelector<HTMLButtonElement>(
      ".cb-save-liju-action",
    );

    if (form === null || button === null) {
      continue;
    }

    panels.set(
      button,
      listenForSavedLiju({ control, form, button, document, fetched }),
    );
  }

  // A badge found held opens its panel where there is room. See
  // `study-standing.ts`.
  if (hasSession(document) && buttons.length > 0) {
    void held(
      fetched,
      buttons.map((button) => button.value),
    )
      .then(async (saved) => {
        for (const button of buttons) {
          drawSavedLiju(button, saved.includes(button.value));
        }

        await Promise.all(
          buttons
            .filter((button) => saved.includes(button.value))
            .map(async (button) => openWhereRoom(panels.get(button), document)),
        );
      })
      .catch(() => undefined);
  }
}
